import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { stripeApi, claimsApi } from '../../firebase/functionsService';
import { useTerms } from '../../hooks/useTerms';
import { cancellationFee } from '../../utils/terms';
import ClaimsList from '../../components/orderer/ClaimsList';
import { acceptBidWithPayment, acceptBidErrorMessage } from '../../stripe/acceptBid';
import { useOrderBids } from '../../hooks/useOrderBids';
import { useOrderInvoice } from '../../hooks/useInvoice';
import Navbar from '../../components/common/Navbar';
import OrdererPhotos from '../../components/common/OrdererPhotos';
import SheetModal, { sheetButtons } from '../../components/common/SheetModal';
import { OrderSupplierLogo } from '../../components/common/SupplierLogo';
import OrderSteps from '../../components/driver/OrderSteps';
import { haversineKm } from '../../utils/priceCalculator';
import { toDate, timeLabel, dayLabel, kmLabel, fullAddress } from '../../utils/orderCardFormat';
import { statusInfo } from '../../utils/orderStatus';
import { orderTitle } from '../../utils/orderTitle';
import { BADGE_CONFIG } from '../../utils/driverBadge';
import { GoogleMap, MarkerF, PolylineF, useJsApiLoader } from '@react-google-maps/api';
import DriverInfoCard from '../../components/orderer/DriverInfoCard';
import DriverTrust from '../../components/orderer/DriverTrust';
import { useDriverInfo } from '../../hooks/useDriverInfo';
import ProgressTimeline from '../../components/orderer/OrderTimeline';
import OrderTimeline from '../../components/common/OrderTimeline';
import { useOrderTimelineData } from '../../hooks/useOrderTimelineData';
import { buildOrderTimeline, FINAL_ORDER_STATUSES } from '../../utils/orderTimeline';
import { equipmentNames } from '../../utils/loadingEquipment';
import { orderTruckText, orderLoadsText, orderLoadSplitText, orderCraneLiftText } from '../../utils/orderTruck';
import { orderHourlyText, orderQtyText } from '../../utils/orderHourly';
import { massAccountLines } from '../../utils/massAccount';
import { COLORS, FONTS } from '../../styles/tokens';
import { TruckAnimation, TruckDriving, useTruckTheme } from '../../components/common/truck';

const num = n => Number(n).toLocaleString('nb-NO');
const kr = n => `kr ${Number(n).toLocaleString('nb-NO')}`;
const initialsOf = name => (name ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?';

function useNow(intervalMs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function countdown(ms) {
  if (ms <= 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  const p = n => String(n).padStart(2, '0');
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
}

export default function OrderDetail({ orders }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const truckTheme = useTruckTheme();

  const order = orders?.find(o => o.id === orderId);
  const { bids, loading: bidsLoading } = useOrderBids(orderId);
  const [error, setError] = useState('');
  // Bunnark: 'accept' (valgt bud), 'delete', 'cancel', 'settings', 'weight', 'confirm'
  const [sheet, setSheet] = useState(null);
  const [acceptBidTarget, setAcceptBidTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sheetError, setSheetError] = useState('');
  const { terms } = useTerms();
  const [claimBusyId, setClaimBusyId] = useState(null);
  const [claimError, setClaimError] = useState('');

  const closeSheet = useCallback(() => { if (!busy) { setSheet(null); setAcceptBidTarget(null); setSheetError(''); } }, [busy]);

  // Vektavvik åpnes automatisk første gang, så bestiller ikke overser det
  const deviationPending = !!order?.pendingAdjustment && !order.pendingAdjustment.approvedByOrderer && order.status !== 'disputed';
  const [deviationShown, setDeviationShown] = useState(false);
  useEffect(() => {
    if (deviationPending && !deviationShown) { setSheet('weight'); setDeviationShown(true); }
  }, [deviationPending, deviationShown]);

  const sortedBids = useMemo(() => [...bids].sort((a, b) => a.amount - b.amount), [bids]);

  // Ferdige oppdrag (fullført, fakturert, refundert, kansellert, i tvist) viser hele forløpet som tidslinje
  const isFinal = FINAL_ORDER_STATUSES.includes(order?.status);
  const history = useOrderTimelineData(orderId, 'orderer', isFinal);
  const timelineEvents = useMemo(
    () => (isFinal ? buildOrderTimeline({ order, bids: history.bids, invoice: history.invoice, creditNotes: history.creditNotes, role: 'orderer' }) : []),
    [isFinal, order, history.bids, history.invoice, history.creditNotes]
  );

  if (!order) {
    return (
      <>
        <Navbar />
        <div style={{ padding: 40, textAlign: 'center', color: COLORS.textLight }}>
          Laster oppdrag…
        </div>
      </>
    );
  }

  const now = new Date();
  const deadline = order.bidDeadline?.toDate?.();
  const deadlinePassed = deadline ? now > deadline : false;
  const isOpen = order.status === 'open';
  const scheduled = toDate(order.scheduledAt);
  const st = statusInfo(order.status);
  const a = order.pickupAddress, b = order.deliveryAddress;
  const routeKm = a?.lat != null && b?.lat != null ? haversineKm(a.lat, a.lng, b.lat, b.lng) : null;
  const tracking = ['matched', 'in_progress', 'delivered_pending', 'disputed', 'completed', 'invoiced'].includes(order.status);
  const canCancelAssigned = order.status === 'matched';
  const pendingClaims = (order.claims ?? []).filter(c => c.status === 'pending');
  const cancelFee = canCancelAssigned && order.driverUid
    ? cancellationFee({ scheduledAt: scheduled, now: new Date(), basePrice: order.agreedPrice ?? order.fixedPrice ?? order.suggestedPrice ?? 0 }, terms)
    : { pct: 0, amount: 0 };

  async function decideClaim(claimId, decision) {
    setClaimBusyId(claimId);
    setClaimError('');
    try {
      await claimsApi.decideClaim({ orderId, claimId, decision });
    } catch (err) {
      console.error(err);
      setClaimError(err?.message || 'Kunne ikke lagre avgjørelsen. Prøv igjen.');
    } finally {
      setClaimBusyId(null);
    }
  }

  async function confirmAcceptBid() {
    if (!acceptBidTarget) return;
    setBusy(true);
    setSheetError('');
    try {
      await acceptBidWithPayment({ orderId, bidId: acceptBidTarget.id, expectedAmount: acceptBidTarget.amount, isTestAccount: order.isTestAccount ?? false });
      setBusy(false);
      setSheet(null);
      setAcceptBidTarget(null);
    } catch (err) {
      console.error(err);
      setSheetError(acceptBidErrorMessage(err));
      setBusy(false);
    }
  }

  async function handleCancelOrder() {
    setBusy(true);
    setSheetError('');
    try {
      await stripeApi.cancelOrderByOrderer(orderId);
      navigate('/orderer');
    } catch (err) {
      console.error(err);
      setSheetError(sheet === 'delete' ? 'Kunne ikke slette oppdraget. Prøv igjen.' : 'Kunne ikke kansellere oppdraget. Prøv igjen.');
      setBusy(false);
    }
  }

  async function setFlag(patch) {
    setError('');
    try {
      await updateDoc(doc(db, 'orders', orderId), patch);
    } catch {
      setError('Kunne ikke endre innstillingen. Prøv igjen.');
    }
  }

  async function approveAdjustment() {
    setBusy(true); setSheetError('');
    try {
      await updateDoc(doc(db, 'orders', orderId), { 'pendingAdjustment.approvedByOrderer': true });
      setBusy(false); setSheet(null);
    } catch { setSheetError('Kunne ikke godkjenne. Prøv igjen.'); setBusy(false); }
  }

  async function dispute() {
    setBusy(true); setSheetError('');
    try {
      await updateDoc(doc(db, 'orders', orderId), { status: 'disputed' });
      setBusy(false); setSheet(null);
    } catch { setSheetError('Feil ved bestridelse. Prøv igjen.'); setBusy(false); }
  }

  async function confirmDelivery() {
    setBusy(true); setSheetError('');
    try {
      await updateDoc(doc(db, 'orders', orderId), { status: 'completed' });
      setBusy(false); setSheet(null);
    } catch { setSheetError('Kunne ikke bekrefte levering. Prøv igjen.'); setBusy(false); }
  }

  const isInvoice = order.paymentMethod === 'invoice';
  const ownerFixedMode = Number.isInteger(order.fixedPrice) && order.fixedPrice > 0;
  const canToggleAuto = isOpen && ownerFixedMode && order.payerType !== 'org';

  return (
    <div className="orderer-ui" style={styles.root}>
      <Navbar />
      <div style={styles.page}>
        <RouteMapHeader order={order} showDriver={['matched', 'in_progress'].includes(order.status)} onBack={() => navigate('/orderer')} />

        <div style={styles.sheet}>
          <div style={styles.metaRow}>
            <div style={styles.meta}>
              #{order.id.slice(-6)}{scheduled && ` · ${dayLabel(scheduled)} ${timeLabel(scheduled)}`}
            </div>
            <span style={{ ...styles.statusBadge, background: st.bg, color: st.color }}>{st.text}</span>
          </div>
          <h1 style={styles.title}>{orderTitle(order)}</h1>
          {order.massDescription && <p style={styles.description}>{order.massDescription}</p>}
          <OrdererPhotos order={order} />

          <div style={styles.routeCard}>
            <div style={styles.stop}>
              <span style={styles.dotPickup} />
              <div style={{ minWidth: 0 }}>
                <div style={styles.stopLabel}>Henting</div>
                <div style={styles.stopAddr}><OrderSupplierLogo order={order} />{fullAddress(a)}</div>
              </div>
              {routeKm != null && <span style={styles.km}>{kmLabel(routeKm)}</span>}
            </div>
            <div style={styles.stop}>
              <span style={styles.dotDelivery} />
              <div style={{ minWidth: 0 }}>
                <div style={styles.stopLabel}>Levering</div>
                <div style={styles.stopAddr}>{fullAddress(b)}</div>
              </div>
            </div>
          </div>

          <div style={styles.kpis}>
            {orderQtyText(order) && <Kpi label="Mengde" value={orderQtyText(order)} />}
            <Kpi label="Levering" value={scheduled ? `${dayLabel(scheduled)} ${timeLabel(scheduled)}` : '—'} />
            {isOpen && deadline && <Kpi label="Frist" value={deadlinePassed ? 'Passert' : timeLabel(deadline)} warn={deadlinePassed} />}
            {ownerFixedMode && !(isOpen && deadline) && <Kpi label="Fastpris" value={kr(order.fixedPrice)} />}
          </div>
          {order.massType !== 'removal' && (
            <div style={styles.kv}><span>Lasteutstyr</span><b>{order.requiresLoadingEquipment ? (equipmentNames(order.loadingEquipment) || 'Sjåfør må ha eget') : 'Finnes på stedet'}</b></div>
          )}
          {orderHourlyText(order) && (
            <div style={styles.kv}><span>Timer</span><b>{orderHourlyText(order)}</b></div>
          )}
          {orderTruckText(order) && (
            <div style={styles.kv}><span>Bil</span><b>{orderTruckText(order)}</b></div>
          )}
          {orderLoadsText(order) && (
            <div style={styles.kv}><span>Lass</span><b>{orderLoadsText(order)}</b></div>
          )}
          {orderLoadSplitText(order) && (
            <div style={styles.kv}><span>Fordeling</span><b>{orderLoadSplitText(order)}</b></div>
          )}
          {orderCraneLiftText(order) && (
            <div style={styles.kv}><span>Kranløft</span><b>{orderCraneLiftText(order).replace('Kranløft: ', '')}</b></div>
          )}
          {massAccountLines(order).map(l => (
            <div key={l.label} style={styles.kv}><span>{l.label}</span><b>{l.value}</b></div>
          ))}

          {isInvoice && (
            <div style={styles.kv}><span>Betaling</span><b>Betales på faktura</b></div>
          )}

          <ClaimsList order={order} onDecide={decideClaim} busyId={claimBusyId} error={claimError} />

          {error && <p role="alert" style={styles.error}>{error}</p>}

          {/* ── Åpent: bud ── */}
          {isOpen && (
            <div style={{ marginTop: 14 }}>
              <div style={styles.bidsHead}>
                <h2 style={styles.h2}>{bidsLoading ? 'Bud' : `${bids.length} ${bids.length === 1 ? 'bud' : 'bud'}`}</h2>
                <span style={{ fontSize: 12, color: deadlinePassed ? COLORS.danger : COLORS.textLight }}>
                  {ownerFixedMode
                    ? `Fastpris-tak ${kr(order.fixedPrice)}`
                    : deadline ? (deadlinePassed ? 'Frist passert' : `Frist ${dayLabel(deadline).toLowerCase()} ${timeLabel(deadline)}`) : ''}
                </span>
              </div>
              {!bidsLoading && bids.length === 0 && (
                <p style={styles.empty}>
                  {ownerFixedMode && order.autoAccept
                    ? `Første sjåfør som godtar fastprisen får oppdraget${order.allowBids ? '. Du kan også få bud under fastprisen.' : '.'}`
                    : ownerFixedMode
                      ? 'Ingen bud ennå. Sjåfører kan by opptil fastprisen.'
                      : 'Ingen bud ennå. Du ser budene her så snart de kommer inn.'}
                </p>
              )}
              {sortedBids.map((bid, i) => (
                <BidRow key={bid.id} bid={bid} first={i === 0}
                        onAccept={() => { setAcceptBidTarget(bid); setSheetError(''); setSheet('accept'); }} />
              ))}
              <div style={styles.footLinks}>
                {canToggleAuto
                  ? <button type="button" style={styles.linkBtn} onClick={() => setSheet('settings')}>Automatisk godkjenning</button>
                  : <span />}
                <button type="button" style={{ ...styles.linkBtn, color: COLORS.danger }} onClick={() => { setSheetError(''); setSheet('delete'); }}>
                  Slett oppdrag
                </button>
              </div>
            </div>
          )}

          {/* ── Tildelt og videre: sporing ── */}
          {tracking && !isFinal && (
            <div style={{ marginTop: 16 }}>
              <OrderSteps status={order.status === 'invoiced' ? 'completed' : order.status} />
            </div>
          )}

          {order.status === 'in_progress' && (
            <div style={styles.drivingBanner}>
              <span aria-hidden="true" style={{ flexShrink: 0 }}><TruckDriving size={72} theme={truckTheme} /></span>
              <div>
                <b>Sjåfør på vei</b>
                <div style={{ fontSize: 13, color: COLORS.textMid }}>Lasset er hentet og er på vei til leveringsstedet.</div>
              </div>
            </div>
          )}

          {order.status === 'disputed' && (
            <div style={styles.noticeWarn}>
              <strong>Tvist åpnet.</strong> Tippi behandler saken og kontakter deg. Betalingen holdes tilbake til den er avgjort.
            </div>
          )}

          {deviationPending && (
            <button type="button" style={styles.actionBanner} onClick={() => setSheet('weight')}>
              <span style={{ flex: 1, textAlign: 'left' }}><b>Vektavvik</b><br /><span style={{ fontSize: 12 }}>Godkjenning kreves</span></span>
              <span style={styles.actionBannerCta}>Se avvik</span>
            </button>
          )}

          {order.status === 'delivered_pending' && (
            <button type="button" style={styles.actionBanner} onClick={() => setSheet('confirm')}>
              <span style={{ flex: 1, textAlign: 'left' }}><b>Sjåfør har registrert levering</b><br /><span style={{ fontSize: 12 }}>Bekreft at massen er levert</span></span>
              <span style={styles.actionBannerCta}>Bekreft</span>
            </button>
          )}

          {['matched', 'in_progress', 'delivered_pending'].includes(order.status) && (
            <div style={{ marginTop: 14 }}>
              <DriverInfoCard driverUid={order.driverUid} />
              {['matched', 'in_progress'].includes(order.status) && <DriverLocationNote order={order} />}
            </div>
          )}

          {tracking && !isFinal && <ProgressTimeline order={order} />}

          {isFinal && <OrderTimeline events={timelineEvents} role="orderer" loading={history.loading} />}

          {['completed', 'invoiced'].includes(order.status) && (
            <div style={styles.success}>
              <TruckAnimation variant="success" theme={truckTheme} size={84} decorative />
              <div>
                <b>Oppdrag fullført</b>
                <div style={{ fontSize: 13, color: COLORS.textMid }}>
                  {order.status === 'invoiced' ? 'Faktura er sendt.' : 'Levering godkjent. Faktura genereres.'}
                </div>
              </div>
            </div>
          )}
          {!isInvoice && ['invoiced', 'completed'].includes(order.status) && <InvoiceSection orderId={orderId} />}

          {canCancelAssigned && (
            <button type="button" style={{ ...styles.linkBtn, color: COLORS.textLight, margin: '18px auto 0', display: 'block' }}
                    onClick={() => { setSheetError(''); setSheet('cancel'); }}>
              Kanseller oppdrag
            </button>
          )}
        </div>
      </div>

      {/* ── Bunnark ── */}
      {sheet === 'accept' && acceptBidTarget && (
        <SheetModal onClose={closeSheet} busy={busy} label="Godta bud">
          <h2 style={sheetButtons.title}>Godta budet på {kr(acceptBidTarget.amount)}?</h2>
          <p style={sheetButtons.text}>
            {acceptBidTarget.driverName || 'Sjåføren'} får oppdraget.{' '}
            {isInvoice
              ? 'Oppdraget betales på faktura.'
              : 'Du betaler etter levering.'}
          </p>
          {sheetError && <p role="alert" style={sheetButtons.error}>{sheetError}</p>}
          <button style={{ ...sheetButtons.gold, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={confirmAcceptBid}>
            {busy ? 'Godtar…' : 'Godta bud'}
          </button>
          <button style={sheetButtons.ghost} disabled={busy} onClick={closeSheet}>Avbryt</button>
        </SheetModal>
      )}

      {(sheet === 'delete' || sheet === 'cancel') && (
        <SheetModal onClose={closeSheet} busy={busy} label={sheet === 'delete' ? 'Slette oppdraget' : 'Kansellere oppdraget'}>
          <h2 style={sheetButtons.title}>{sheet === 'delete' ? 'Slette oppdraget?' : 'Kansellere oppdraget?'}</h2>
          <p style={sheetButtons.text}>
            {sheet === 'delete'
              ? `#${order.id.slice(-6)}${bids.length ? ` og alle ${bids.length} bud` : ''} fjernes. Dette kan ikke angres.`
              : 'Sjåføren varsles og oppdraget kanselleres.'}
          </p>
          {sheet === 'cancel' && cancelFee.amount > 0 && (
            <p style={sheetButtons.text}>
              Avbestilling nå koster {num(cancelFee.amount)} kr ({num(cancelFee.pct)} %) som sjåføren kan kreve.
            </p>
          )}
          {sheetError && <p role="alert" style={sheetButtons.error}>{sheetError}</p>}
          <button style={{ ...sheetButtons.red, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={handleCancelOrder}>
            {busy ? 'Vent…' : sheet === 'delete' ? 'Slett oppdrag' : 'Kanseller oppdrag'}
          </button>
          <button style={sheetButtons.ghost} disabled={busy} onClick={closeSheet}>Behold</button>
        </SheetModal>
      )}

      {sheet === 'settings' && (
        <SheetModal onClose={closeSheet} label="Automatisk godkjenning">
          <h2 style={sheetButtons.title}>Automatisk godkjenning</h2>
          <SwitchRow
            title="Godta første sjåfør automatisk"
            hint="Godkjente sjåfører får oppdraget med en gang til fastprisen."
            on={order.autoAccept === true}
            onChange={v => setFlag(v ? { autoAccept: true } : { autoAccept: false, allowBids: false })}
          />
          {order.autoAccept === true && (
            <SwitchRow
              title="Tillat bud under fastprisen"
              hint="Fastprisen er det mest du betaler. Sjåfører kan også by lavere, og du velger blant budene."
              on={order.allowBids === true}
              onChange={v => setFlag({ allowBids: v })}
            />
          )}
          {error && <p role="alert" style={sheetButtons.error}>{error}</p>}
          <button style={{ ...sheetButtons.ghost, marginTop: 16 }} onClick={closeSheet}>Ferdig</button>
        </SheetModal>
      )}

      {sheet === 'weight' && deviationPending && (
        <SheetModal onClose={closeSheet} busy={busy} label="Vektavvik">
          <h2 style={sheetButtons.title}>Vektavvik</h2>
          <p style={sheetButtons.text}>
            Sjåfør registrerte <b style={{ color: COLORS.textDark }}>{num(order.pendingAdjustment.actualTons)} tonn</b> (estimert {num(order.pendingAdjustment.estimatedTons)} tonn).
            Avvik: {order.pendingAdjustment.deviationPct} %.
          </p>
          <div style={styles.sheetBox}>
            <div style={styles.sheetRow}><span>Estimert vekt</span><span style={styles.sheetNum}>{num(order.pendingAdjustment.estimatedTons)} tonn</span></div>
            <div style={{ ...styles.sheetRow, borderTop: `1px solid ${COLORS.border}` }}><span>Registrert vekt</span><span style={styles.sheetNum}>{num(order.pendingAdjustment.actualTons)} tonn</span></div>
            <div style={{ ...styles.sheetRow, borderTop: `1px solid ${COLORS.border}` }}>
              <span>Prisjustering</span>
              <span style={{ ...styles.sheetNum, fontSize: 28, color: order.pendingAdjustment.priceAdjustment > 0 ? COLORS.dangerText : COLORS.successText }}>
                {order.pendingAdjustment.priceAdjustment > 0 ? '+' : '−'} {kr(Math.abs(order.pendingAdjustment.priceAdjustment))}
              </span>
            </div>
          </div>
          {sheetError && <p role="alert" style={sheetButtons.error}>{sheetError}</p>}
          <button style={{ ...sheetButtons.gold, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={approveAdjustment}>
            {busy ? 'Godkjenner…' : 'Godkjenn justering'}
          </button>
          <button style={sheetButtons.ghost} disabled={busy} onClick={dispute}>Bestrid</button>
        </SheetModal>
      )}

      {sheet === 'confirm' && order.status === 'delivered_pending' && (
        <ConfirmDeliverySheet
          order={order} busy={busy} error={sheetError} hasPendingClaims={pendingClaims.length > 0}
          onConfirm={confirmDelivery} onDispute={dispute} onClose={closeSheet}
        />
      )}
    </div>
  );
}

function ConfirmDeliverySheet({ order, busy, error, hasPendingClaims, onConfirm, onDispute, onClose }) {
  const now = useNow(1000);
  const deadline = order.deliveryConfirmDeadline?.toDate?.();
  const photos = order.deliveryPhotoUrls ?? [];
  return (
    <SheetModal onClose={onClose} busy={busy} label="Bekreft levering">
      <h2 style={sheetButtons.title}>Bekreft levering</h2>
      <p style={sheetButtons.text}>Bekreft at massen er levert som avtalt. Betaling trekkes ved bekreftelse.</p>
      {(photos.length > 0 || order.receiverSignatureUrl) && (
        <div style={styles.thumbs}>
          {photos.slice(0, 3).map(url => (
            <img key={url} src={url} alt="Bilde av leveransen" style={styles.thumb} />
          ))}
          {order.receiverSignatureUrl && (
            <img src={order.receiverSignatureUrl} alt="Signatur fra mottaker" style={{ ...styles.thumb, objectFit: 'contain', background: COLORS.white }} />
          )}
        </div>
      )}
      {hasPendingClaims && (
        <div style={styles.countdown}>
          <b>Du har krav å ta stilling til</b>
        </div>
      )}
      {deadline && (
        <div style={styles.countdown}>
          <span style={{ flex: 1 }}>Godkjennes automatisk om</span>
          <b style={{ fontFamily: 'var(--font-mono)' }}>{countdown(deadline.getTime() - now)}</b>
        </div>
      )}
      {error && <p role="alert" style={sheetButtons.error}>{error}</p>}
      <button style={{ ...sheetButtons.gold, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={onConfirm}>
        {busy ? 'Bekrefter…' : 'Bekreft levering'}
      </button>
      <button style={sheetButtons.ghost} disabled={busy} onClick={onDispute}>Bestrid levering</button>
    </SheetModal>
  );
}

function SwitchRow({ title, hint, on, onChange }) {
  return (
    <div style={styles.switchRow}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: COLORS.textMid, lineHeight: 1.35, marginTop: 2 }}>{hint}</div>
      </div>
      <button type="button" role="switch" aria-checked={on} aria-label={title} onClick={() => onChange(!on)}
              style={{ ...styles.track, background: on ? COLORS.accent : COLORS.border }}>
        <span style={{ ...styles.knob, transform: on ? 'translateX(20px)' : 'none' }} />
      </button>
    </div>
  );
}

function Kpi({ label, value, warn }) {
  return (
    <div style={styles.kpi}>
      <div style={{ fontSize: 11, color: COLORS.textLight }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 14, color: warn ? COLORS.danger : COLORS.textDark }}>{value}</div>
    </div>
  );
}

function BidDriverTrust({ driverUid }) {
  const { documented, languages } = useDriverInfo(driverUid);
  return <DriverTrust documented={documented} languages={languages} />;
}

function BidRow({ bid, onAccept, first }) {
  const badgeInfo = BADGE_CONFIG[bid.driverBadge] ?? BADGE_CONFIG.new;
  return (
    <div style={styles.bidRow}>
      <div style={styles.avatar} aria-hidden="true">{initialsOf(bid.driverName)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {bid.driverName || 'Sjåfør'}
          <span style={styles.badge}>{badgeInfo.label}</span>
        </div>
        <BidDriverTrust driverUid={bid.driverUid} />
        {bid.driverCompletedOrders > 0 && (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: COLORS.textLight, textTransform: 'uppercase' }}>
            {bid.driverCompletedOrders} oppdrag
          </div>
        )}
        {bid.message && <div style={{ fontSize: 12.5, color: COLORS.textMid, marginTop: 2 }}>«{bid.message}»</div>}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={styles.bidAmount}>{Number(bid.amount).toLocaleString('nb-NO')}</div>
        <button type="button" onClick={onAccept}
                style={first ? styles.acceptGold : styles.acceptOutline}>
          Godta bud
        </button>
      </div>
    </div>
  );
}

function InvoiceSection({ orderId }) {
  const { invoice, loading } = useOrderInvoice(orderId);
  if (loading) return null;
  if (!invoice) return <p style={styles.empty}>Faktura genereres…</p>;
  const paid = invoice.status === 'captured';
  return (
    <div style={styles.invoice}>
      <h2 style={styles.h2}>Faktura</h2>
      <InvoiceLine label="Grunnbeløp" value={kr(invoice.baseAmount)} />
      {invoice.adjustmentAmount !== 0 && (
        <InvoiceLine label="Vektjustering" value={`${invoice.adjustmentAmount > 0 ? '+ ' : '− '}${kr(Math.abs(invoice.adjustmentAmount))}`} />
      )}
      <InvoiceLine label="Tippi-gebyr (10 %), trekkes fra sjåførens utbetaling" value={kr(invoice.tippiFee)} muted />
      <div style={styles.invoiceTotal}>
        <span style={{ fontWeight: 700 }}>Totalt fakturert</span>
        <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 34, lineHeight: 1 }}>{kr(invoice.totalAmount)}</span>
      </div>
      <div style={{ fontSize: 12, color: COLORS.textLight }}>
        Status: <strong style={{ color: paid ? COLORS.successText : COLORS.warningText }}>{paid ? 'Betalt' : invoice.status === 'pending' ? 'Behandles' : invoice.status}</strong>. Alle beløp er eks. mva.
      </div>
    </div>
  );
}

function InvoiceLine({ label, value, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderTop: `1px solid ${COLORS.border}`, fontSize: 14, color: muted ? COLORS.textLight : COLORS.textDark }}>
      <span style={{ color: muted ? COLORS.textLight : COLORS.textMid }}>{label}</span>
      <b style={{ whiteSpace: 'nowrap' }}>{value}</b>
    </div>
  );
}

// Må ha nøyaktig samme loader-innstillinger som resten av appen (AddressInput, OrderMap).
const MAPS_LIBRARIES = ['places'];
// Eldre enn dette regnes posisjonen som utdatert (sjåføren har trolig lukket appen)
const STALE_LOCATION_MS = 5 * 60 * 1000;

function DriverLocationNote({ order }) {
  const location = order.driverLocation;
  const updatedAt = location?.updatedAt?.toDate?.();
  const now = useNow(30000);
  if (!location?.lat) {
    return <p style={styles.locNote}>Venter på posisjon fra sjåfør…</p>;
  }
  const ageMs = updatedAt ? now - updatedAt.getTime() : null;
  const stale = ageMs != null && ageMs > STALE_LOCATION_MS;
  const minutes = ageMs != null ? Math.max(0, Math.round(ageMs / 60000)) : null;
  if (minutes == null) return null;
  return (
    <p style={{ ...styles.locNote, color: stale ? COLORS.warningText : COLORS.textLight }}>
      {stale
        ? `Posisjonen ble sist oppdatert for ${minutes} min siden. Sjåføren har trolig lukket appen.`
        : minutes === 0 ? 'Posisjon oppdatert nå nettopp' : `Posisjon oppdatert for ${minutes} min siden`}
    </p>
  );
}

// Kart øverst med rute mellom henting og levering, og sjåførens posisjon når oppdraget er i gang
function RouteMapHeader({ order, showDriver, onBack }) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '',
    libraries: MAPS_LIBRARIES,
  });
  const p = order.pickupAddress, d = order.deliveryAddress, loc = showDriver ? order.driverLocation : null;
  const hasRoute = p?.lat != null && d?.lat != null;
  const onLoad = useCallback(map => {
    if (!hasRoute || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: p.lat, lng: p.lng });
    bounds.extend({ lat: d.lat, lng: d.lng });
    if (loc?.lat != null) bounds.extend({ lat: loc.lat, lng: loc.lng });
    map.fitBounds(bounds, { top: 70, bottom: 70, left: 50, right: 50 });
  }, [hasRoute, p, d, loc]);

  const circle = isLoaded && window.google ? window.google.maps.SymbolPath.CIRCLE : null;
  return (
    <div style={styles.mapWrap}>
      {isLoaded && hasRoute ? (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={{ lat: p.lat, lng: p.lng }}
          zoom={11}
          onLoad={onLoad}
          options={{ disableDefaultUI: true, gestureHandling: 'cooperative', clickableIcons: false }}
        >
          <PolylineF path={[{ lat: p.lat, lng: p.lng }, { lat: d.lat, lng: d.lng }]}
                     options={{ strokeColor: '#1a1a2e', strokeOpacity: 0.85, strokeWeight: 3, geodesic: true }} />
          <MarkerF position={{ lat: p.lat, lng: p.lng }} title="Henting"
                   icon={{ path: circle, scale: 7, fillColor: '#1a1a2e', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 3 }} />
          <MarkerF position={{ lat: d.lat, lng: d.lng }} title="Levering"
                   icon={{ path: circle, scale: 7, fillColor: '#fdc149', fillOpacity: 1, strokeColor: '#1a1a2e', strokeWeight: 3 }} />
          {loc?.lat != null && (
            <MarkerF position={{ lat: loc.lat, lng: loc.lng }} title="Sjåfør"
                     icon={{ path: circle, scale: 10, fillColor: '#fdc149', fillOpacity: 1, strokeColor: '#1a1a2e', strokeWeight: 3 }} zIndex={10} />
          )}
        </GoogleMap>
      ) : (
        <div style={{ width: '100%', height: '100%', background: '#e9e3d0' }} />
      )}
      <button type="button" onClick={onBack} aria-label="Tilbake til oppdrag" style={styles.backBtn}>
        <svg width="12" height="20" viewBox="0 0 12 20" fill="none" aria-hidden="true"><path d="M10 2L2 10l8 8" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
    </div>
  );
}

const styles = {
  root:        { minHeight: '100vh', background: COLORS.bg, display: 'flex', flexDirection: 'column' },
  page:        { maxWidth: 640, margin: '0 auto', width: '100%', paddingBottom: 40 },
  mapWrap:     { position: 'relative', height: 230, background: '#e9e3d0' },
  backBtn:     { position: 'absolute', top: 'max(16px, env(safe-area-inset-top))', left: 16, width: 44, height: 44, borderRadius: '50%', border: 'none', background: COLORS.white, boxShadow: '0 2px 8px rgba(0,0,0,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 5 },
  sheet:       { position: 'relative', marginTop: -26, background: COLORS.bg, borderRadius: '26px 26px 0 0', padding: '18px 20px 0' },
  metaRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
  meta:        { fontFamily: 'var(--font-mono)', fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: '0.04em' },
  statusBadge: { borderRadius: 20, padding: '4px 11px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },
  title:       { fontFamily: FONTS.display, fontSize: 36, lineHeight: 1.02, fontWeight: 700, color: COLORS.textDark, margin: '4px 0 10px' },
  description: { fontSize: 14, color: COLORS.textMid, margin: '-4px 0 10px' },
  routeCard:   { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 },
  stop:        { display: 'flex', alignItems: 'flex-start', gap: 12 },
  stopLabel:   { fontSize: 11, color: COLORS.textLight },
  stopAddr:    { fontSize: 15, fontWeight: 600, color: COLORS.textDark },
  km:          { marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: COLORS.textLight, alignSelf: 'flex-end', whiteSpace: 'nowrap' },
  dotPickup:   { width: 10, height: 10, borderRadius: '50%', background: COLORS.primary, marginTop: 11, flexShrink: 0 },
  dotDelivery: { width: 12, height: 12, borderRadius: '50%', background: COLORS.accent, border: `2px solid ${COLORS.primary}`, marginTop: 10, flexShrink: 0, boxSizing: 'border-box' },
  kpis:        { display: 'flex', gap: 8, margin: '10px 0' },
  kpi:         { flex: 1, minWidth: 0, background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: '8px 12px' },
  kv:          { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '11px 0', borderTop: `1px solid ${COLORS.border}`, fontSize: 14, color: COLORS.textMid },
  error:       { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 12px', borderRadius: 10, fontSize: 14, margin: '8px 0' },
  bidsHead:    { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  h2:          { fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, margin: 0, color: COLORS.textDark },
  empty:       { fontSize: 14, color: COLORS.textMid, lineHeight: 1.5, padding: '14px 0' },
  bidRow:      { display: 'flex', alignItems: 'center', gap: 11, padding: '12px 0', borderBottom: `1px solid ${COLORS.border}` },
  avatar:      { width: 40, height: 40, borderRadius: '50%', background: '#efeadd', color: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.display, fontWeight: 700, fontSize: 17, flexShrink: 0 },
  badge:       { background: '#efeadd', color: '#1a1a2e', borderRadius: 20, padding: '2px 8px', fontSize: 10.5, fontWeight: 700 },
  bidAmount:   { fontFamily: FONTS.display, fontWeight: 700, fontSize: 26, lineHeight: 1, color: COLORS.textDark },
  acceptGold:  { marginTop: 6, background: COLORS.accent, color: '#1a1a2e', border: 'none', borderRadius: 18, padding: '7px 14px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' },
  acceptOutline: { marginTop: 6, background: 'transparent', color: COLORS.textDark, border: `1.5px solid ${COLORS.primary}`, borderRadius: 18, padding: '6px 13px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' },
  footLinks:   { display: 'flex', justifyContent: 'space-between', marginTop: 16 },
  linkBtn:     { background: 'none', border: 'none', padding: '6px 0', fontSize: 13.5, fontWeight: 600, color: COLORS.textDark, cursor: 'pointer', fontFamily: 'inherit' },
  noticeWarn:  { background: COLORS.dangerBg, color: COLORS.dangerText, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: 14, padding: '12px 14px', fontSize: 14, lineHeight: 1.45, marginTop: 14 },
  actionBanner:{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: COLORS.warningBg, border: `1px solid ${COLORS.warningBorder}`, color: COLORS.warningText, borderRadius: 16, padding: '12px 14px', fontSize: 14, fontFamily: 'inherit', cursor: 'pointer', marginTop: 14 },
  actionBannerCta: { background: COLORS.accent, color: '#1a1a2e', borderRadius: 16, padding: '7px 14px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' },
  locNote:     { fontSize: 12, color: COLORS.textLight, margin: '8px 0 0' },
  success:     { display: 'flex', alignItems: 'center', gap: 12, background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 14, marginTop: 16, color: COLORS.textDark },
  drivingBanner:{ display: 'flex', alignItems: 'center', gap: 12, background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: '10px 14px', marginTop: 14, color: COLORS.textDark },
  successBadge:{ width: 40, height: 40, borderRadius: '50%', background: COLORS.accent, color: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, flexShrink: 0 },
  invoice:     { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: '16px', marginTop: 14 },
  invoiceTotal:{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '12px 0', borderTop: `1px solid ${COLORS.border}` },
  sheetBox:    { background: COLORS.bg, borderRadius: 16, padding: '4px 14px', marginBottom: 16 },
  sheetRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '10px 0', fontSize: 14, color: COLORS.textMid },
  sheetNum:    { fontFamily: FONTS.display, fontWeight: 700, fontSize: 22, color: COLORS.textDark },
  thumbs:      { display: 'flex', gap: 8, margin: '0 0 12px' },
  thumb:       { width: 88, height: 70, objectFit: 'cover', borderRadius: 10, border: `1px solid ${COLORS.border}` },
  countdown:   { display: 'flex', alignItems: 'center', gap: 10, background: COLORS.warningBg, border: `1px solid ${COLORS.warningBorder}`, borderRadius: 14, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: COLORS.warningText },
  switchRow:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderTop: `1px solid ${COLORS.border}`, color: COLORS.textDark },
  track:       { position: 'relative', width: 50, height: 30, borderRadius: 15, border: 'none', cursor: 'pointer', flexShrink: 0, padding: 0, transition: 'background 0.15s' },
  knob:        { position: 'absolute', top: 3, left: 3, width: 24, height: 24, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'transform 0.15s' },
};
